/**
 * Candidate-facing theme helpers (logo, brand colours, derived text colour).
 *
 * Relocated from `assessment/pages/utils` into the shared utils layer so
 * product pages (e.g. `pages/unsubscribe-revamp`) can derive theme details
 * without importing from a secondary app. `assessment/pages/utils` now
 * re-exports these for its own callers.
 */

interface ThemeData {
  data?: {
    logo_url?: string;
    theme?: {
      primary?: { backgroundColor?: string };
      secondary?: { backgroundColor?: string };
    };
  };
}

/** Parse a hex (`#rrggbb`/`rrggbb`) or `rgb(...)`/gradient string to `[r,g,b]`. */
export const getRgbCodes = (color?: string): number[] => {
  if (color?.includes('gradient')) {
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      return [
        parseInt(rgbMatch[1]),
        parseInt(rgbMatch[2]),
        parseInt(rgbMatch[3]),
      ];
    }
    return [];
  }

  const result = color?.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);

  if (!result) {
    return [];
  }
  const red = isFinite(parseInt(`${result[1]}`, 16))
    ? parseInt(`${result[1]}`, 16)
    : 0;
  const green = isFinite(parseInt(`${result[2]}`, 16))
    ? parseInt(`${result[2]}`, 16)
    : 0;
  const blue = isFinite(parseInt(`${result[3]}`, 16))
    ? parseInt(`${result[3]}`, 16)
    : 0;

  return [red, green, blue];
};

const getRgbWithOpacity = (color: string, opacity = 0.1): string => {
  const [r, g, b] = getRgbCodes(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const getTextColorForBackground = (bgColor: string): string => {
  const [r, g, b] = getRgbCodes(bgColor);

  // Calculate the YIQ value (brightness formula)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Return black for bright backgrounds, and white for dark backgrounds
  return yiq >= 128 ? '#000000' : '#FFFFFF';
};

export const getThemeDetails = ({ themeData }: { themeData?: ThemeData }) => {
  const defaultPrimaryColor = '#F95D2D';
  const defaultConfig = {
    primaryColor: defaultPrimaryColor,
    secondaryColor: '#FBF6EE',
    logoUrl: 'https://assets-1.kula.ai/images/logo/logo-1-b@3x.png',
    textColor: '#fff',
    tertiaryColor: getRgbWithOpacity(defaultPrimaryColor, 0.1),
  };
  return {
    primaryColor:
      themeData?.data?.theme?.primary?.backgroundColor ||
      defaultConfig.primaryColor,
    secondaryColor:
      themeData?.data?.theme?.secondary?.backgroundColor ||
      defaultConfig.secondaryColor,
    logoUrl: themeData?.data?.logo_url || defaultConfig.logoUrl,
    textColor: themeData?.data?.theme?.primary?.backgroundColor
      ? getTextColorForBackground(
          themeData?.data?.theme?.primary?.backgroundColor
        )
      : defaultConfig.textColor,
    tertiaryColor: themeData?.data?.theme?.primary?.backgroundColor
      ? getRgbWithOpacity(themeData?.data?.theme?.primary?.backgroundColor, 0.1)
      : defaultConfig.tertiaryColor,
  };
};
